package dev.lingvo.site.components.studio.editor

/** A semantic action keeps platform event parsing out of panels and command tests. */
internal enum class StudioActionType {
    SELECT_TOOL, TEXT_TOOL, RECTANGLE_TOOL, HAND_TOOL, TEMPORARY_HAND, NUDGE, LANGUAGE, SLOT,
    UNDO, REDO, DUPLICATE, COPY, PASTE, CUT, DELETE, GROUP, UNGROUP, FORWARD, BACKWARD,
    TO_FRONT, TO_BACK, SELECT_ALL, ENTER, PARENT, ESCAPE, FIT, ACTUAL_SIZE, ZOOM_SELECTION,
    ZOOM_IN, ZOOM_OUT, FOCUS_REGION, SHORTCUTS,
}

/** Directional values avoid encoding physical key names into controller logic. */
internal data class StudioAction(val type: StudioActionType, val amount: Int = 0, val dx: Int = 0, val dy: Int = 0)

/** Code is layout independent; key is used only for the printable question mark. */
internal data class StudioShortcutInput(
    val code: String, val key: String = "", val control: Boolean = false, val meta: Boolean = false,
    val alt: Boolean = false, val shift: Boolean = false, val composing: Boolean = false,
    val textField: Boolean = false, val stageFocused: Boolean = true, val defaultPrevented: Boolean = false,
)

/** Global editor chords work from fields; local chords preserve native text editing. */
internal fun resolveStudioShortcut(input: StudioShortcutInput): StudioAction? {
    if (input.composing || input.defaultPrevented) return null
    val command = input.control || input.meta
    fun action(type: StudioActionType, amount: Int = 0) = StudioAction(type, amount)
    if (command && input.alt) return null
    if (input.alt && !input.shift) return when (input.code) {
        "ArrowLeft" -> action(StudioActionType.LANGUAGE, -1)
        "ArrowRight" -> action(StudioActionType.LANGUAGE, 1)
        "ArrowUp" -> action(StudioActionType.SLOT, -1)
        "ArrowDown" -> action(StudioActionType.SLOT, 1)
        else -> null
    }
    if (input.alt) return null
    if (!command && input.code == "F6") return action(StudioActionType.FOCUS_REGION, if (input.shift) -1 else 1)
    if (command && input.code == "KeyZ") return action(if (input.shift) StudioActionType.REDO else StudioActionType.UNDO)
    if (input.control && !input.meta && !input.shift && input.code == "KeyY") return action(StudioActionType.REDO)
    if (input.textField) return null
    if (!command && input.key == "?") return action(StudioActionType.SHORTCUTS)
    if (!input.stageFocused) return null
    if (command) return when (input.code) {
        "KeyD" -> action(StudioActionType.DUPLICATE)
        "KeyC" -> action(StudioActionType.COPY)
        "KeyV" -> action(StudioActionType.PASTE)
        "KeyX" -> action(StudioActionType.CUT)
        "KeyG" -> action(if (input.shift) StudioActionType.UNGROUP else StudioActionType.GROUP)
        "BracketRight" -> action(if (input.shift) StudioActionType.TO_FRONT else StudioActionType.FORWARD)
        "BracketLeft" -> action(if (input.shift) StudioActionType.TO_BACK else StudioActionType.BACKWARD)
        "KeyA" -> action(StudioActionType.SELECT_ALL)
        "Equal" -> action(StudioActionType.ZOOM_IN)
        "Minus" -> action(StudioActionType.ZOOM_OUT)
        else -> null
    }
    val distance = if (input.shift) 10 else 1
    return when (input.code) {
        "ArrowLeft" -> StudioAction(StudioActionType.NUDGE, dx = -distance)
        "ArrowRight" -> StudioAction(StudioActionType.NUDGE, dx = distance)
        "ArrowUp" -> StudioAction(StudioActionType.NUDGE, dy = -distance)
        "ArrowDown" -> StudioAction(StudioActionType.NUDGE, dy = distance)
        "Enter" -> action(if (input.shift) StudioActionType.PARENT else StudioActionType.ENTER)
        "Escape" -> action(StudioActionType.ESCAPE)
        "Delete", "Backspace" -> action(StudioActionType.DELETE)
        "Space" -> if (!input.shift) action(StudioActionType.TEMPORARY_HAND) else null
        "Digit1" -> if (input.shift) action(StudioActionType.FIT) else null
        "Digit0" -> if (input.shift) action(StudioActionType.ACTUAL_SIZE) else null
        "Digit2" -> if (input.shift) action(StudioActionType.ZOOM_SELECTION) else null
        "KeyV" -> if (!input.shift) action(StudioActionType.SELECT_TOOL) else null
        "KeyT" -> if (!input.shift) action(StudioActionType.TEXT_TOOL) else null
        "KeyR" -> if (!input.shift) action(StudioActionType.RECTANGLE_TOOL) else null
        "KeyH" -> if (!input.shift) action(StudioActionType.HAND_TOOL) else null
        else -> null
    }
}
